import { useEffect, useRef, useState } from "react"
import ModuleLayout from "@/components/admin/ModuleLayout"
import Label from "@/components/form/Label"
import { getInputClasses } from "@/components/form/input/InputField"
import Button from "@/components/ui/button/Button"
import api from "@/lib/api"
import { toastError, toastSuccess } from "@/lib/toast"
import { accountTabs } from "./accountTabs"
import { LoadState } from "@/components/ui/LoadState"

interface Profile {
  name: string
  bio: string | null
  phone: string | null
  country: string | null
  avatarUrl: string | null
}

const inputCls = getInputClasses({})

function initials(name: string): string {
  return name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase()
}

export default function AccountProfilePage() {
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let cancelled = false
    api.get("/auth/me").then((res) => {
      if (cancelled) return
      const data = res.data.data
      setProfile({ name: data.name, bio: data.bio ?? null, phone: data.phone ?? null, country: data.country ?? null, avatarUrl: data.avatarUrl ?? null })
    }).catch((err) => {
      toastError(err?.response?.data?.error ?? "No se pudo cargar tu perfil.")
    }).finally(() => {
      if (!cancelled) setLoading(false)
    })
    return () => { cancelled = true }
  }, [])

  async function onSave() {
    if (!profile) return
    setSaving(true)
    try {
      await api.put("/auth/profile", {
        name: profile.name,
        bio: profile.bio || null,
        phone: profile.phone || null,
        country: profile.country || null,
      })
      toastSuccess("Perfil actualizado.")
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? "No se pudo guardar el perfil.")
    } finally {
      setSaving(false)
    }
  }

  async function onAvatarSelected(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ""
    if (!file || !profile) return

    const allowed = ["image/jpeg", "image/png", "image/webp"]
    if (!allowed.includes(file.type)) {
      toastError("Solo se permiten imágenes JPEG, PNG o WEBP.")
      return
    }

    setUploadingAvatar(true)
    try {
      const uploadRes = await api.post("/auth/profile/avatar-upload-url", { contentType: file.type })
      const { uploadUrl, avatarUrl } = uploadRes.data.data as { uploadUrl: string; avatarUrl: string }

      const putRes = await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": file.type }, body: file })
      if (!putRes.ok) throw new Error("No se pudo subir la imagen")

      await api.put("/auth/profile", { avatarUrl })
      // Cache-bust: avatarUrl es la misma URL siempre (mismo userId), así que
      // un <img> ya montado no vuelve a pedirla — se agrega un query param
      // solo para forzar el refresco visual inmediato.
      setProfile({ ...profile, avatarUrl: `${avatarUrl}?t=${Date.now()}` })
      toastSuccess("Foto de perfil actualizada.")
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? "No se pudo actualizar la foto de perfil.")
    } finally {
      setUploadingAvatar(false)
    }
  }

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Mi cuenta"]}
      title="Mi cuenta"
      subtitle="Administra tu perfil, tu contraseña y tus integraciones personales."
      tabs={accountTabs("profile")}
      metrics={[]}
    >
      {loading || !profile ? (
        <LoadState />
      ) : (
        <div className="max-w-xl space-y-6 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingAvatar}
              className="group relative size-16 shrink-0 overflow-hidden rounded-full border border-gray-200 disabled:opacity-60 dark:border-gray-700"
              title="Cambiar foto de perfil"
            >
              {profile.avatarUrl ? (
                <img src={profile.avatarUrl} alt={profile.name} className="size-full object-cover" />
              ) : (
                <span className="flex size-full items-center justify-center bg-gray-100 text-sm font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                  {initials(profile.name)}
                </span>
              )}
              <span className="absolute inset-0 flex items-center justify-center bg-black/40 text-[11px] font-medium text-white opacity-0 group-hover:opacity-100">
                {uploadingAvatar ? "Subiendo…" : "Cambiar"}
              </span>
            </button>
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onAvatarSelected} />
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Foto de perfil</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">JPEG, PNG o WEBP</p>
            </div>
          </div>

          <div>
            <Label>Nombre</Label>
            <input
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <Label>Bio</Label>
            <textarea
              value={profile.bio ?? ""}
              onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
              rows={3}
              className={inputCls}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Teléfono</Label>
              <input
                value={profile.phone ?? ""}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <Label>País</Label>
              <input
                value={profile.country ?? ""}
                onChange={(e) => setProfile({ ...profile, country: e.target.value })}
                className={inputCls}
              />
            </div>
          </div>

          <Button onClick={onSave} disabled={saving} size="sm">
            {saving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </div>
      )}
    </ModuleLayout>
  )
}
